import React from "react";
import { X, CheckCircle2, Info, Zap, ArrowRight } from "lucide-react";

export default function DetailModal({ open, onClose, data }) {
  if (!open || !data) return null;

  // v2 Data Mapping (with safe fallbacks)
  const angle = data.angle || "Detailed Breakdown";
  const logic = data.logic || data.why;
  const protocol = data.protocol || data.what || [];
  const protocolLabel = data.protocolLabel || "The Protocol";
  const tip = data.proTip || data.tip;
  const ctaLabel = data.cta?.label || "GOT IT";

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 100,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "1rem",
        backgroundColor: "rgba(0,0,0,0.85)", // Darker, cleaner backdrop
        backdropFilter: "blur(8px)",
        animation: "fadeIn 0.2s ease-out",
      }}
      onClick={onClose}
    >
      {/* CARD CONTAINER */}
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: "520px",
          background: "#0b0c10",
          border: "1px solid rgba(255,255,255,0.1)",
          borderRadius: "24px",
          boxShadow: "0 40px 80px -12px rgba(0, 0, 0, 0.6)",
          overflow: "hidden",
          position: "relative",
          animation: "slideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1)",
        }}
      >
        {/* HERO HEADER */}
        <div
          style={{
            background:
              "radial-gradient(circle at 50% 0%, rgba(34, 197, 94, 0.12), transparent 80%)",
            padding: "36px 32px 24px",
            borderBottom: "1px solid rgba(255,255,255,0.06)",
            textAlign: "center",
          }}
        >
          {/* Phase Tag */}
          <div
            style={{
              fontSize: "10px",
              textTransform: "uppercase",
              letterSpacing: "2px",
              color: "var(--muted)",
              marginBottom: "12px",
              fontWeight: 800,
            }}
          >
            {data.phase?.toUpperCase()}
          </div>

          <h2
            style={{
              fontSize: "32px",
              fontWeight: 800,
              margin: "0 0 8px 0",
              color: "#fff",
              letterSpacing: "-0.03em",
              lineHeight: "1.1",
            }}
          >
            {data.title}
          </h2>

          <div
            style={{
              fontSize: "15px",
              color: "var(--brand)",
              fontWeight: 600,
              letterSpacing: "0.5px",
            }}
          >
            {data.subtitle}
          </div>

          {/* Tags */}
          {data.tags && (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                gap: 8,
                marginTop: 20,
              }}
            >
              {data.tags.map((tag, i) => (
                <span
                  key={i}
                  className="chip"
                  style={{
                    fontSize: "11px",
                    padding: "4px 12px",
                    background: "rgba(255,255,255,0.05)",
                    border: "1px solid rgba(255,255,255,0.1)",
                    color: "var(--ink)",
                  }}
                >
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* CLOSE BTN */}
        <button
          onClick={onClose}
          style={{
            position: "absolute",
            top: "20px",
            right: "20px",
            background: "rgba(255,255,255,0.05)",
            border: "none",
            borderRadius: "50%",
            width: "36px",
            height: "36px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#fff",
            cursor: "pointer",
            transition: "background 0.2s",
          }}
          onMouseEnter={(e) =>
            (e.currentTarget.style.background = "rgba(255,255,255,0.1)")
          }
          onMouseLeave={(e) =>
            (e.currentTarget.style.background = "rgba(255,255,255,0.05)")
          }
        >
          <X size={20} />
        </button>

        {/* CONTENT BODY */}
        <div
          style={{
            padding: "32px",
            display: "flex",
            flexDirection: "column",
            gap: "28px",
          }}
        >
          {/* 1. ANGLE / LOGIC */}
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginBottom: 10,
                color: "var(--muted)",
                fontSize: "11px",
                fontWeight: 800,
                textTransform: "uppercase",
                letterSpacing: "1px",
              }}
            >
              <Info size={14} color="var(--brand)" /> The Logic
            </div>
            <div
              style={{
                fontSize: "18px",
                fontWeight: 700,
                color: "#fff",
                marginBottom: "8px",
                lineHeight: "1.3",
              }}
            >
              {angle}
            </div>
            <p
              style={{
                margin: 0,
                fontSize: "15px",
                lineHeight: "1.6",
                color: "var(--muted)",
              }}
            >
              {logic}
            </p>
          </div>

          {/* 2. PROTOCOL (SAFE RENDER) */}
          <div
            style={{
              background: "rgba(255,255,255,0.03)",
              padding: "20px",
              borderRadius: "16px",
              border: "1px solid rgba(255,255,255,0.05)",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginBottom: 12,
                color: "var(--muted)",
                fontSize: "11px",
                fontWeight: 800,
                textTransform: "uppercase",
                letterSpacing: "1px",
              }}
            >
              <CheckCircle2 size={14} color="var(--brand)" /> {protocolLabel}
            </div>

            {Array.isArray(protocol) ? (
              <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
                {protocol.map((item, i) => (
                  <li
                    key={i}
                    style={{
                      display: "flex",
                      gap: "12px",
                      marginBottom: "10px",
                      fontSize: "14px",
                      color: "#e2e8f0",
                      alignItems: "flex-start",
                    }}
                  >
                    <span
                      style={{
                        color: "var(--brand)",
                        opacity: 0.6,
                        marginTop: "6px",
                        fontSize: "10px",
                      }}
                    >
                      ●
                    </span>
                    <span style={{ lineHeight: "1.5" }}>{item}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p
                style={{
                  margin: 0,
                  fontSize: "14px",
                  color: "#e2e8f0",
                  lineHeight: "1.5",
                }}
              >
                {protocol}
              </p>
            )}
          </div>

          {/* 3. PRO TIP */}
          <div
            style={{ display: "flex", gap: "14px", alignItems: "flex-start" }}
          >
            <div
              style={{
                minWidth: "32px",
                height: "32px",
                borderRadius: "8px",
                background: "rgba(234, 179, 8, 0.1)",
                border: "1px solid rgba(234, 179, 8, 0.2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#eab308",
              }}
            >
              <Zap size={18} fill="currentColor" />
            </div>
            <div>
              <div
                style={{
                  fontSize: "11px",
                  fontWeight: 800,
                  color: "#fff",
                  marginBottom: "4px",
                  textTransform: "uppercase",
                  letterSpacing: "1px",
                }}
              >
                Pro Tip
              </div>
              <div
                style={{
                  fontSize: "14px",
                  color: "var(--muted)",
                  fontStyle: "italic",
                  lineHeight: "1.5",
                }}
              >
                "{tip}"
              </div>
            </div>
          </div>
        </div>

        {/* FOOTER */}
        <div
          style={{
            borderTop: "1px solid rgba(255,255,255,0.06)",
            padding: "20px 32px",
            textAlign: "center",
            background: "#0f172a",
          }}
        >
          <button
            onClick={onClose}
            style={{
              width: "100%",
              padding: "14px",
              borderRadius: "12px",
              background: "var(--brand)",
              color: "#022c22",
              fontWeight: 800,
              border: "none",
              fontSize: "13px",
              cursor: "pointer",
              letterSpacing: "1px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              transition: "transform 0.1s",
            }}
            onMouseDown={(e) =>
              (e.currentTarget.style.transform = "scale(0.98)")
            }
            onMouseUp={(e) => (e.currentTarget.style.transform = "scale(1)")}
          >
            {ctaLabel} <ArrowRight size={16} />
          </button>
        </div>
      </div>

      <style>{`
        @keyframes slideUp {
          from { transform: translateY(20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>
    </div>
  );
}
